import React, { useState } from 'react';
import { AlertCircle, Check, Loader2, Mail, MapPin, Phone } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { Textarea } from './ui/Textarea';
import { serviceGroups } from '../data/services';
import { site } from '../data/site';
type Status = 'idle' | 'submitting' | 'success' | 'error';
const fieldClass = 'mt-2 h-12 rounded-none border-ivory/20 bg-ink-800 text-ivory placeholder:text-ivory/30 focus-visible:border-brass focus-visible:ring-brass/30';
export function ContactSection() {
  const [status, setStatus] = useState<Status>('idle');
  const [scope, setScope] = useState<string>(serviceGroups[0].title);
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const name = String(data.get('name') ?? '').trim();
    const email = String(data.get('email') ?? '').trim();
    if (!name || !email.includes('@')) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 900);
  };
  return <section id="contact" className="bg-ink py-24 text-ivory lg:py-32" data-id="element-24" data-mp="2p64bdu1e0w3">
      <div className="mx-auto grid max-w-[1400px] gap-16 px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-10" data-id="element-25" data-mp="goe9qogsm58q">
        <div data-id="element-26" data-mp="cjoen1tykuov">
          <div className="flex items-center gap-4" data-id="element-27" data-mp="cnc860zelh3l">
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-brass-light" data-id="element-28" data-mp="qk8v3rceief7">
              Book a Visit
            </span>
            <span className="h-px w-16 rule-brass" aria-hidden="true" data-id="element-29" data-mp="ljtkv8nf1n1g" />
          </div>
          <h2 className="mt-6 font-display text-4xl leading-[1.05] sm:text-5xl lg:text-[3.4rem]" data-id="element-30" data-mp="10aai0ft0pqsj">
            Tell us what needs
            <br data-id="element-31" data-mp="ve3fkwztyxfc" />
            <span className="italic text-brass-light" data-id="element-32" data-mp="19b8od4qxiaea">finishing</span>.
          </h2>
          <p className="mt-6 max-w-md leading-relaxed text-ivory/60" data-id="element-33" data-mp="14cfdzl6xuzic">
            Send a note with your address and a rough count of doors, drawers, or pieces. We reply
            within one business day with availability and a flat quote.
          </p>

          <dl className="mt-12 space-y-6 border-t border-ink-600 pt-10" data-id="element-34" data-mp="6ylyr3yietgi">
            <div className="flex items-start gap-4" data-id="element-35" data-mp="azfkprse5y6r">
              <dt className="mt-0.5" data-id="element-36" data-mp="axurq6lq6nb0">
                <Phone className="h-4 w-4 text-brass" aria-hidden="true" data-id="element-37" data-mp="fqnx8wp0pzo5" />
                <span className="sr-only" data-id="element-38" data-mp="kphbirc4pupd">Phone</span>
              </dt>
              <dd data-id="element-39" data-mp="krldh3qgwjn0">
                <a href={`tel:${site.phone.replace(/[^0-9]/g, '')}`} className="text-lg text-ivory transition-colors hover:text-brass-light" data-id="element-40" data-mp="poljjenbhme4">
                  {site.phone}
                </a>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ivory/40" data-id="element-41" data-mp="yo8jrx4b8m5l">
                  {site.hours}
                </p>
              </dd>
            </div>
            <div className="flex items-start gap-4" data-id="element-42" data-mp="30fv79ooztyxb">
              <dt className="mt-0.5" data-id="element-43" data-mp="30igaoi4xwmx6">
                <Mail className="h-4 w-4 text-brass" aria-hidden="true" data-id="element-44" data-mp="2sxdsb2yclrwy" />
                <span className="sr-only" data-id="element-45" data-mp="2o6dy45gm5ny5">Email</span>
              </dt>
              <dd className="text-lg" data-id="element-46" data-mp="2o52pnsc83s0p">
                <a href={`mailto:${site.email}`} className="break-all text-ivory transition-colors hover:text-brass-light" data-id="element-47" data-mp="2j717raqj9yqt">
                  {site.email}
                </a>
              </dd>
            </div>
            <div className="flex items-start gap-4" data-id="element-48" data-mp="3btvrg5fj0u38">
              <dt className="mt-0.5" data-id="element-49" data-mp="36wn5dtr9c5wp">
                <MapPin className="h-4 w-4 text-brass" aria-hidden="true" data-id="element-50" data-mp="36xongi6oxsnx" />
                <span className="sr-only" data-id="element-51" data-mp="32tywxn6gh109">Service area</span>
              </dt>
              <dd className="text-lg text-ivory" data-id="element-52" data-mp="2xx03l6um8y34">{site.serviceArea}</dd>
            </div>
          </dl>
        </div>

        <div className="border border-ink-600 bg-ink-800/60 p-8 lg:p-12" data-id="element-53" data-mp="1rgwioyrtwnj9">
          {status === 'success' ? <div role="status" className="flex h-full min-h-[420px] flex-col items-start justify-center" data-id="element-54" data-mp="1mfgyf8xh6l1x">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-brass" data-id="element-55" data-mp="3f46isx047xgy">
                <Check className="h-6 w-6 text-ink" aria-hidden="true" data-id="element-56" data-mp="31dbgy4o1agmv" />
              </span>
              <h3 className="mt-8 font-display text-3xl text-ivory" data-id="element-57" data-mp="35edhqpxln0qy">Request received.</h3>
              <p className="mt-4 max-w-sm leading-relaxed text-ivory/60" data-id="element-58" data-mp="3t47tt5a3gla5">
                Thank you — we have your details. Expect a reply within one business day with
                availability and a flat quote. For anything urgent, call {site.phone}.
              </p>
              <Button variant="outline" onClick={() => setStatus('idle')} className="mt-10 h-12 rounded-none border-ivory/25 bg-transparent px-6 font-mono text-[11px] uppercase tracking-[0.2em] text-ivory hover:bg-ivory hover:text-ink" data-id="element-59" data-mp="3k39o8ik0c5pg">
                Send another request
              </Button>
            </div> : <form onSubmit={handleSubmit} noValidate className="space-y-6" data-id="element-60" data-mp="254vp11itmblq">
              <div className="grid gap-6 sm:grid-cols-2" data-id="element-61" data-mp="213tnge1flngl">
                <div data-id="element-62" data-mp="2f4mf8r0cm4g0">
                  <Label htmlFor="name" className="font-mono text-[10px] uppercase tracking-[0.2em] text-ivory/60" data-id="element-63" data-mp="2f4w9wrwc8zs1">
                    Full name
                  </Label>
                  <Input id="name" name="name" autoComplete="name" placeholder="Jane Whitfield" className={fieldClass} data-id="element-64" data-mp="1mfysss3v590v" />
                </div>
                <div data-id="element-65" data-mp="1hhx8k3upb3nt">
                  <Label htmlFor="phone" className="font-mono text-[10px] uppercase tracking-[0.2em] text-ivory/60" data-id="element-66" data-mp="1hip5bi01b7fo">
                    Phone
                  </Label>
                  <Input id="phone" name="phone" type="tel" autoComplete="tel" placeholder="(512) 555-0100" className={fieldClass} data-id="element-67" data-mp="1re0me6yx0qql" />
                </div>
              </div>

              <div data-id="element-68" data-mp="yqnwbmtjq0sj">
                <Label htmlFor="email" className="font-mono text-[10px] uppercase tracking-[0.2em] text-ivory/60" data-id="element-69" data-mp="yr60r6qd3lbj">
                  Email
                </Label>
                <Input id="email" name="email" type="email" autoComplete="email" placeholder="jane@example.com" className={fieldClass} data-id="element-70" data-mp="33rqp9iejemj1" />
              </div>

              <div data-id="element-71" data-mp="38iyyadbyg5vo">
                <Label htmlFor="address" className="font-mono text-[10px] uppercase tracking-[0.2em] text-ivory/60" data-id="element-72" data-mp="38kjsg1ucxmt0">
                  Project address
                </Label>
                <Input id="address" name="address" placeholder="Street, city" className={fieldClass} data-id="element-73" data-mp="2fv3177ih4byd" />
              </div>

              <fieldset data-id="element-74" data-mp="2kuyxekkxiz3d">
                <legend className="font-mono text-[10px] uppercase tracking-[0.2em] text-ivory/60" data-id="element-75" data-mp="2oug6rp3h8gdt">
                  What do you need?
                </legend>
                <div className="mt-3 flex flex-wrap gap-2" data-id="element-76" data-mp="2tuudn3tcdka9">
                  {serviceGroups.map(group => {
                const selected = scope === group.title;
                return <button key={group.id} type="button" onClick={() => setScope(group.title)} aria-pressed={selected} className={`border px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors ${selected ? 'border-brass bg-brass text-ink' : 'border-ivory/20 text-ivory/60 hover:border-ivory/50 hover:text-ivory'}`} data-id="element-77" data-mp="91h6fuyxji4f">
                        {group.title}
                      </button>;
              })}
                </div>
                <input type="hidden" name="scope" value={scope} data-id="element-78" data-mp="3imdonx7o8xy8" />
              </fieldset>

              <div data-id="element-79" data-mp="3ioqfkzveqinf">
                <Label htmlFor="details" className="font-mono text-[10px] uppercase tracking-[0.2em] text-ivory/60" data-id="element-80" data-mp="1kwccvnnsdtf4">
                  Details
                </Label>
                <Textarea id="details" name="details" rows={5} placeholder="e.g. 54 cabinet pulls, 3 knobs, two Wi-Fi deadbolts, and a gallery wall of 9 frames." className="mt-2 rounded-none border-ivory/20 bg-ink-800 text-ivory placeholder:text-ivory/30 focus-visible:border-brass focus-visible:ring-brass/30" data-id="element-81" data-mp="1gswiyz33q764" />
              </div>

              {status === 'error' && <p role="alert" className="flex items-start gap-2 text-sm text-brass-light" data-id="element-82" data-mp="1ge88var5wdex">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-83" data-mp="1lfnwnakq4y10" />
                  Please add your name and a valid email so we can reply.
                </p>}

              <Button type="submit" disabled={status === 'submitting'} className="h-14 w-full rounded-none bg-brass font-mono text-[11px] uppercase tracking-[0.22em] text-ink hover:bg-brass-light" data-id="element-84" data-mp="1cezfi701ahjw">
                {status === 'submitting' ? <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-85" data-mp="3gu1imsellur4" />
                    Sending
                  </> : 'Request a consultation'}
              </Button>
              <p className="text-center font-mono text-[10px] uppercase tracking-[0.18em] text-ivory/35" data-id="element-86" data-mp="3kx7o3xtvzhwj">
                No obligation · Flat quotes · Fully insured
              </p>
            </form>}
        </div>
      </div>
    </section>;
}